import { useQuery } from 'react-query'
import { datadogRum } from '@datadog/browser-rum'
import { useVercelServerlessAxios } from '@/axios/vercelServerlessConfig'
import { Case, ApiErrorResponse } from '../types'

/**
 * The key prefix every variant of a case's cached details shares, whatever
 * flags the fetch passed. A surface that only reads what is already cached
 * (the Messages chat titles) matches on this, so it keeps finding the case
 * however a caller threads the flags.
 */
export const getCaseDetailsQueryPrefix = (caseId?: string): string[] =>
  caseId ? ['caseData', caseId] : ['caseData']

/**
 * The react-query key a case's details are cached under. Keep its head in
 * step with getCaseDetailsQueryPrefix.
 */
export const getCaseDetailsQueryKey = (
  caseId: string,
  includeLeadScore = false,
  withholdClientContact = false
) => ['caseData', caseId, includeLeadScore, withholdClientContact] as const

export const useCaseDetails = (
  caseId: string,
  setApiError: React.Dispatch<React.SetStateAction<ApiErrorResponse | null>>,
  includeLeadScore = false,
  // Withhold Client Contact UI flag, forwarded to the BFF. Masking and the
  // withheld status-line cells follow the data, never this.
  withholdClientContact = false
) => {
  const [{ loading: axiosLoading }, executeRequest] = useVercelServerlessAxios({
    url: `/api/cases`,
    method: 'GET',
    params: {
      caseId,
      includeLeadScore: includeLeadScore.toString(),
      // Sent only when on, so the request is unchanged with the flag off.
      ...(withholdClientContact && { withholdClientContact: '1' }),
    },
  })

  const fetchCaseData = async (): Promise<Case> => {
    try {
      const { data } = await executeRequest()

      // Map backend snake_case to frontend camelCase
      return {
        ...data,
        qualityScore: data.quality_score,
        marketFit: data.market_fit,
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response) {
        setApiError({
          status: error.response.status,
          message: error.response.data.error,
        })
      }
      throw error
    }
  }

  const { data, isLoading, isFetching, error, refetch } = useQuery<Case, Error>(
    getCaseDetailsQueryKey(caseId, includeLeadScore, withholdClientContact),
    fetchCaseData,
    {
      enabled: !!caseId,
      onError: err => {
        datadogRum.addError(err)
      },
    }
  )

  return {
    data,
    isLoading,
    axiosLoading,
    isFetching,
    error,
    refetch,
  }
}
